import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MfaCheckFailedNotice } from '@/components/auth/mfa-check-failed-notice';
import { BrandLoader } from '@/components/shared/brand-loader';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { assertNever } from '@/lib/assert-never';
import { revokeAbandonedSession } from '@/lib/auth-session-revoke';
import { checkMFARequirementStrict, isLockContentionError } from '@/lib/auth-utils';
import { cn } from '@/lib/utils';

import { decideRouting, type RoutingDecision } from './auth-callback-decision';

/**
 * Advisory state for the callback. `check_failed` means the OAuth exchange left
 * a live session behind but whether it still owes a TOTP step-up could not be
 * determined - the one state that must not be resolved by letting the user in.
 * `routing_failed` means the step-up answered but the lookups that decide
 * where the user belongs could not - the one state that must not be resolved
 * by signing them out.
 */
type StepUpNotice = 'none' | 'check_failed' | 'routing_failed';

const MFA_VERIFY_PATH = '/auth/mfa-verify';

/** Log prefix for this page's share of the shared revoke helper. */
const REVOKE_LABEL = 'OAuth callback abandon';
const DENY_REVOKE_LABEL = 'OAuth callback deny';

const REQUEST_ACCESS_PATH = '/request-access';

/** A signed-in account other than `userId`; unknown and signed-out are not. */
const isOtherAccount = (seen: string | null | undefined, userId: string): boolean =>
  typeof seen === 'string' && seen !== userId;

function hasOAuthErrorInLocation(): boolean {
  const hashParams = new URLSearchParams(globalThis.location.hash.slice(1));
  const searchParams = new URLSearchParams(globalThis.location.search);
  const params = hashParams.has('error') ? hashParams : searchParams;
  return params.has('error');
}

/**
 * AuthCallback Component
 *
 * Handles OAuth authentication callbacks and redirects users based on their role.
 * For existing users: redirects to appropriate dashboard
 * For new users: validates invitation before allowing access
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { user, loading } = useAuth();
  const [notice, setNotice] = useState<StepUpNotice>('none');
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const unmountedRef = useRef(false);
  /**
   * Monotonic stamp for each pass through the decision. The effect below is
   * keyed on `user`, and auth-context hands out a freshly parsed object on
   * INITIAL_SESSION, SIGNED_IN and TOKEN_REFRESHED alike, so a pass can be
   * superseded mid-check. Only the newest pass may write.
   */
  const runSeqRef = useRef(0);
  /**
   * The pass that armed the obligation, or 0 when nothing is owed. Owed means
   * the session exists but nothing has admitted it: a step-up still pending,
   * or a routing decision left open behind the advisory. A run id rather than
   * a boolean: it is both the gate and the receipt, so whichever of the
   * unmount cleanup and the check's own continuation arrives first revokes
   * and the other becomes a no-op - and a superseded pass cannot
   * discharge an obligation a newer one owns. A ref, not state: every reader
   * runs in an async continuation or an unmount cleanup, where a render-scoped
   * value is already a render stale.
   */
  const owedByRunRef = useRef(0);
  /**
   * The account an armed obligation was incurred for. The revoke signs out
   * whatever session auth-js holds when it runs, so an obligation must not
   * outlive the identity it was armed over (see the auth-event effect below).
   */
  const owedUserIdRef = useRef<string | null>(null);
  /**
   * Latched the moment a pass decides - admitted, or handed off to the
   * challenge route. The decision is terminal FOR BACKGROUND PASSES and a
   * later one MUST NOT re-arm over it: admission navigates, that navigation
   * unmounts this page, and an obligation armed inside that window makes the
   * cleanup revoke the very session the user was just admitted on. The latch
   * reopens in two places - a user-pressed retry (`handleRetry`), which can
   * only exist on a page still showing the advisory, i.e. one whose decision
   * demonstrably did NOT admit; and a change of account, since a decision
   * made for one user decides nothing for another.
   */
  const settledRef = useRef(false);
  /**
   * The user the session currently belongs to. auth-context can replace the
   * account without an intervening sign-out, so a pass must check it is still
   * deciding for the current user - the run stamp alone cannot tell, because a
   * settled latch returns before a new pass takes a stamp.
   */
  const currentUserIdRef = useRef<string | null>(null);
  /**
   * The account auth-js's session belongs to, as its subscribers last heard -
   * `undefined` until the first event. auth-js switches sessions ahead of the
   * context re-rendering, so in that window only this knows a pass is deciding
   * for an account that no longer holds the session.
   */
  const sdkUserIdRef = useRef<string | null | undefined>(undefined);

  const oauthError = hasOAuthErrorInLocation();

  // Surface the provider error immediately. Without an early branch the user
  // sees a spinner that bounces silently back to /auth/login with no
  // explanation.
  useEffect(() => {
    if (!oauthError) {
      return;
    }
    toast({
      title: t('oauth.failedTitle'),
      description: t('oauth.failedDescription'),
      variant: 'destructive',
    });
    Promise.resolve(navigate('/auth/login', { replace: true })).catch(() => null);
  }, [oauthError, navigate, t]);

  /**
   * Revoke the session if - and only if - it is still owed: a step-up pending,
   * or a routing decision left open behind the advisory. The obligation is
   * both the gate and the receipt: whichever of the unmount
   * cleanup and the check's own continuation arrives first revokes, and the
   * other becomes a no-op instead of a second sign-out racing the first.
   */
  const abandonOwedSession = useCallback((): void => {
    if (owedByRunRef.current === 0) {
      return;
    }
    // Discharged by this call whatever the revoke's outcome - a second attempt
    // would only race it, and nothing is left mounted to retry in.
    owedByRunRef.current = 0;
    void revokeAbandonedSession(REVOKE_LABEL);
  }, []);

  /**
   * The cleanup's handle on that revoke. An effect event rather than the
   * callback itself: a dependency array holding the callback would re-run the
   * effect on any identity change, and this effect's cleanup signs a user out.
   */
  const compensateOwedStepUp = useEffectEvent((): void => {
    abandonOwedSession();
  });

  useEffect(() => {
    // auth-js notifies subscribers as it switches sessions, ahead of the
    // context's own asynchronous processing - the window in which the page
    // still renders the old account while storage already holds a new one.
    // An obligation incurred for the old account is dropped here, so the
    // unmount revoke cannot sign out the account that replaced it. The
    // callback only touches refs: awaiting auth-js inside it would deadlock.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' && session === null) {
        // The replay every new subscriber gets. It carries null when auth-js
        // could not read or refresh the session - a retryable failure that can
        // leave the account persisted - so it says nothing about who holds it.
        return;
      }
      sdkUserIdRef.current = session?.user.id ?? null;
      if (owedByRunRef.current !== 0 && sdkUserIdRef.current !== owedUserIdRef.current) {
        owedByRunRef.current = 0;
      }
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Reset, not just set-on-cleanup: a remount reuses the same ref objects (a
    // StrictMode mount -> cleanup -> mount does exactly that). A ref left pinned
    // `true` makes the continuation below sign out a live, wanted session, and a
    // latched decision leaves the remount with no decision left to make. Both
    // resets are synchronous inside the mount effect, so no continuation of the
    // previous mount can interleave between them.
    unmountedRef.current = false;
    settledRef.current = false;

    return () => {
      unmountedRef.current = true;
      // This page has no sheet to dismiss: a browser Back, a link, or a
      // programmatic redirect are its only exits, and each of them lands here
      // over a session the OAuth exchange has already persisted. Reload and tab
      // close run nothing at all - the ADR 0068 residual, same as every other
      // flow.
      compensateOwedStepUp();
    };
  }, []);

  /**
   * Retire the advisory as part of settling.
   *
   * A notice offering a retry for a decision already made is stale UI, and the
   * retry is the dangerous half of it: pressing it reopens the latch and arms a
   * fresh obligation over the session this pass has just admitted or handed
   * off, and the unmount that admission causes then revokes it. The button is
   * only reachable while the notice is up, so clearing it AT the decision is
   * what makes the race unreachable rather than merely unlikely.
   */
  const dismissStaleNotice = useCallback((): void => {
    setNotice('none');
  }, []);

  /**
   * Whether auth-js still holds this account's session, as far as its
   * subscribers have heard. Before the first event there is nothing to
   * contradict the context, so the answer is yes.
   */
  const holdsSession = useCallback(
    (userId: string): boolean =>
      sdkUserIdRef.current === undefined || sdkUserIdRef.current === userId,
    [],
  );

  /**
   * Whether a pass may still write: the newest run, on a mounted page, deciding
   * for the account the session belongs to now - in the context and in auth-js.
   */
  const isPassCurrent = useCallback(
    (run: number, userId: string): boolean =>
      run === runSeqRef.current &&
      !unmountedRef.current &&
      currentUserIdRef.current === userId &&
      holdsSession(userId),
    [holdsSession],
  );

  /**
   * Leave the routing decision open, with a way forward. The session is kept -
   * signing it out would bounce a member on a lookup that merely failed to
   * answer - but it is owed again: nothing has admitted it, so leaving the page
   * with the advisory up revokes it rather than leaving a live session behind
   * with nothing rendered over it. The settled latch stays shut to background
   * passes; only the user's retry reopens it (`handleRetry`).
   */
  const leaveRoutingUndecided = useCallback(
    (run: number, userId: string): void => {
      if (!isPassCurrent(run, userId)) {
        return;
      }
      owedByRunRef.current = run;
      owedUserIdRef.current = userId;
      setNotice('routing_failed');
    },
    [isPassCurrent],
  );

  /**
   * The single exit for a failure inside the decision below. WHICH failure it
   * is gets read off the obligation rather than off the error: still armed
   * means the check never settled, and the answer to that is never a redirect
   * into the app; discharged means the check answered and only the routing
   * after it failed, which leaves the routing undecided - never a sign-out.
   */
  const recoverFromStepUpFailure = useCallback(
    (run: number, userId: string, error: unknown): void => {
      if (owedByRunRef.current === run) {
        // The session still owes a step-up. Contention settles nothing either,
        // so it RESTORES the same state rather than returning past it: an early
        // return there strands a live aal1 session behind a spinner with no
        // retry to press. It just does not deserve naming in the log.
        if (!isLockContentionError(error)) {
          console.error('OAuth callback step-up check failed');
        }
        setNotice('check_failed');
        return;
      }
      // POST-discharge: the step-up already answered, and what failed is the
      // routing between discharge and app entry. Contention included, it
      // settles nothing, so it RESTORES a way forward rather than returning
      // past it: the settled latch (rightly) blocks every background pass,
      // which makes a bare return here a spinner nothing will ever replace.
      if (!isLockContentionError(error)) {
        console.error('OAuth callback routing failed');
      }
      leaveRoutingUndecided(run, userId);
    },
    [leaveRoutingUndecided],
  );

  /**
   * Carry out a routing decision. Every exit that leaves the page discharges
   * first (the caller already has), so the unmount it causes revokes nothing.
   */
  const actOnRouting = useCallback(
    async (run: number, userId: string, decision: RoutingDecision): Promise<void> => {
      switch (decision.kind) {
        case 'admit': {
          Promise.resolve(navigate(decision.path, { replace: true })).catch(() => null);
          return;
        }
        case 'deny': {
          // Three authoritative negatives: revoke THIS sign-in attempt (scope
          // local; the user may hold legitimate sessions elsewhere).
          // Through the shared revoke rather than a bare signOut(): auth-js
          // reports a failed sign-out on the resolved channel, and only the
          // helper's fallbacks guarantee the stored session is gone before
          // the redirect.
          await revokeAbandonedSession(DENY_REVOKE_LABEL);
          // Signed out is expected - this sign-out causes it - so only a
          // DIFFERENT account stops the redirect: one that signed in while the
          // sign-out was in flight must not be sent to /request-access.
          if (
            run !== runSeqRef.current ||
            unmountedRef.current ||
            isOtherAccount(sdkUserIdRef.current, userId) ||
            isOtherAccount(currentUserIdRef.current, userId)
          ) {
            return;
          }
          Promise.resolve(navigate(REQUEST_ACCESS_PATH, { replace: true })).catch(() => null);
          return;
        }
        case 'undecided': {
          leaveRoutingUndecided(run, userId);
          return;
        }
        default: {
          assertNever(decision);
        }
      }
    },
    [leaveRoutingUndecided, navigate],
  );

  /**
   * What happens between the exchanged session and app entry. Held outside the
   * effect so the retry can re-run exactly this - the check and its three
   * outcomes - with no second OAuth round trip.
   */
  const resolveStepUp = useCallback(
    async (userId: string): Promise<void> => {
      if (!holdsSession(userId)) {
        // auth-js already holds another account's session: arming here would
        // put this account's obligation over it.
        return;
      }
      if (settledRef.current) {
        // Already decided. A pass that starts here would arm an obligation over
        // a session the previous pass has just admitted or handed off, and the
        // unmount that admission causes would then revoke it.
        return;
      }
      // Stamped and armed BEFORE the lookup rather than beside its answer: the
      // exchange has already persisted a session, and the round trip below is a
      // window in which that session is live with nothing mounted over it.
      const run = ++runSeqRef.current;
      owedByRunRef.current = run;
      owedUserIdRef.current = userId;
      try {
        // No redundant getSession() - user from useAuth() being non-null
        // confirms a session exists, and the check below fails naturally if it
        // is actually invalid.
        const requirement = await checkMFARequirementStrict();

        if (
          run !== runSeqRef.current ||
          currentUserIdRef.current !== userId ||
          !holdsSession(userId)
        ) {
          // Superseded while in flight, or the account changed under the check. The newer pass owns the obligation and
          // will answer for it; writing anything here - notice, redirect, or a
          // discharge - would settle its state on this pass's stale answer.
          return;
        }

        if (unmountedRef.current) {
          // The decision has nowhere to land: no notice left to render, no
          // redirect worth firing. The session it would have decided over is
          // revoked instead - a no-op when the unmount cleanup has already
          // done it, the only revoke if the obligation was settled in between.
          abandonOwedSession();
          return;
        }

        switch (requirement.status) {
          case 'indeterminate': {
            // FAIL CLOSED. The lossy `checkMFARequirement()` answers
            // `needsMFA: false` on every error path, so the previous code here
            // walked a TOTP-enrolled user straight into the app whenever the
            // AAL lookup blipped - over a session already persisted, which RLS
            // admits because it is AAL-blind (ADR 0068). The session is KEPT
            // rather than revoked so the retry re-reads the level without a
            // second trip through the provider.
            //
            // A direct navigation meanwhile meets the same answer: the route
            // guard reads the strict check too and, on web, denies
            // `indeterminate` (components/auth/protected-route.tsx).
            owedByRunRef.current = run;
            owedUserIdRef.current = userId;
            setNotice('check_failed');
            return;
          }
          case 'required': {
            // Discharged AT the handoff, not left armed: the challenge lives on
            // its own route, so the unmount this navigation causes IS the
            // step-up mounting rather than the user walking away from it. Left
            // armed, the cleanup above would revoke the very session the
            // challenge page is about to elevate.
            settledRef.current = true;
            owedByRunRef.current = 0;
            dismissStaleNotice();
            Promise.resolve(navigate(MFA_VERIFY_PATH, { replace: true })).catch(() => null);
            return;
          }
          case 'not_required': {
            // Discharged: no step-up is owed, so the session is complete. Set
            // rather than assumed - a retry after a `check_failed` arrives here
            // with the obligation armed. Latched before the await: the routing
            // lookups below are several round trips long, and their own
            // redirect unmounts this page. An undecided answer re-arms it
            // (`leaveRoutingUndecided`).
            settledRef.current = true;
            owedByRunRef.current = 0;
            dismissStaleNotice();
            const decision = await decideRouting(userId);
            if (!isPassCurrent(run, userId)) {
              // Superseded, unmounted, or the account changed under the
              // lookups: this answer decides nothing for the current session.
              return;
            }
            await actOnRouting(run, userId, decision);
            return;
          }
          default: {
            // A new member of MFARequirement must not fall through to app
            // entry; this makes the missing branch a compile error instead.
            assertNever(requirement);
          }
        }
      } catch (error) {
        if (
          run !== runSeqRef.current ||
          unmountedRef.current ||
          currentUserIdRef.current !== userId ||
          !holdsSession(userId)
        ) {
          // Superseded, or the cleanup already read this obligation and
          // compensated it; there is no state left to restore either way.
          return;
        }
        recoverFromStepUpFailure(run, userId, error);
      }
    },
    [
      abandonOwedSession,
      actOnRouting,
      dismissStaleNotice,
      holdsSession,
      isPassCurrent,
      navigate,
      recoverFromStepUpFailure,
    ],
  );

  const startStepUpDecision = useEffectEvent((userId: string): void => {
    void resolveStepUp(userId);
  });

  useEffect(() => {
    const previousUserId = currentUserIdRef.current;
    currentUserIdRef.current = user?.id ?? null;
    if (owedByRunRef.current !== 0 && owedUserIdRef.current !== currentUserIdRef.current) {
      // The context may publish a new account before auth-js's event reaches
      // this page; either signal alone drops an obligation armed for another.
      owedByRunRef.current = 0;
    }
    if (user && previousUserId !== user.id) {
      // A different account now owns the session. The previous account's
      // decision - admitted, denied or left open - decides nothing for it.
      settledRef.current = false;
    }

    if (loading) {
      return;
    }

    // The error-branch effect above owns this case; bail to avoid a
    // racing duplicate navigate.
    if (oauthError) {
      return;
    }

    if (!user) {
      Promise.resolve(navigate('/auth/login', { replace: true })).catch(() => null);
      return;
    }

    startStepUpDecision(user.id);
  }, [user, loading, navigate, oauthError]);

  const handleRetry = (): void => {
    if (!user) {
      return;
    }
    // A user-pressed retry may reopen a settled decision; a background
    // re-fire may not. The latch exists to stop a pass arming an obligation
    // over a session the previous pass just admitted or handed off - but this
    // button only renders while the advisory is up, i.e. on a decision that
    // demonstrably did NOT admit (admission navigates and unmounts). Beside
    // the mount reset, the only other unlatch is a change of account; this one
    // is what lets a restored advisory actually lead somewhere.
    settledRef.current = false;
    // Back to the spinner while the same decision re-runs, against the same
    // session: the credential is never asked for twice.
    setNotice('none');
    // The button this click came from unmounts with the notice, which would
    // drop focus to <body>. Park it on the live region, which stays mounted
    // either way, so the state change is announced and focus keeps a place.
    noticeRef.current?.focus();
    void resolveStepUp(user.id);
  };

  const noticeShown = notice !== 'none';

  // Loading spinner while the redirect is being determined, replaced by the
  // advisory once the step-up check or the routing after it has come back
  // unable to answer.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6">
      {noticeShown ? null : <BrandLoader size="lg" />}
      <MfaCheckFailedNotice
        ref={noticeRef}
        // The live region stays mounted so its content is announced when it
        // arrives; the gap it opens under the spinner must not.
        className={cn('max-w-sm text-center text-sm text-tertiary', noticeShown && 'mt-6')}
        message={notice === 'routing_failed' ? t('oauth.routingFailed') : undefined}
        show={noticeShown}
        onRetry={handleRetry}
      />
    </div>
  );
};

export default AuthCallback;
